import { IS_NO_IP_ONLY_HTTPS } from 'constants/constants';
import { required } from 'utils/errorsTextHelper';
import * as yup from 'yup';

import { INTEGRATION_SETTINGS_FIELDS } from './integrationSettingsConstants';

export interface IntegrationField {
  name: string;
  label: string;
  type?: 'text' | 'password' | 'boolean';
  required?: boolean;
  disabled?: boolean;
  enableCopy?: boolean;
  placeholder?: string;
}

export interface IntegrationFormValues {
  [key: string]: string | boolean | undefined | number;
}

export const integrationFields: Record<string, IntegrationField> = {
  [INTEGRATION_SETTINGS_FIELDS.AWARDING_V2_API_URL]: {
    name: INTEGRATION_SETTINGS_FIELDS.AWARDING_V2_API_URL,
    label: 'Grant Award Callback',
    type: 'text',
    required: false,
    placeholder: 'Enter endpoint URL'
  },
  [INTEGRATION_SETTINGS_FIELDS.ORDERS_REPORTING_API_URL]: {
    name: INTEGRATION_SETTINGS_FIELDS.ORDERS_REPORTING_API_URL,
    label: 'Grant Award Callback',
    type: 'text',
    required: false,
    placeholder: 'Enter endpoint URL'
  },
  [INTEGRATION_SETTINGS_FIELDS.PLAYER_INFO_SYNC_URL]: {
    name: INTEGRATION_SETTINGS_FIELDS.PLAYER_INFO_SYNC_URL,
    label: 'Personalization API URL',
    type: 'text',
    required: false
  },
  [INTEGRATION_SETTINGS_FIELDS.PLAYERS_AUTH_WEBHOOK]: {
    name: INTEGRATION_SETTINGS_FIELDS.PLAYERS_AUTH_WEBHOOK,
    label: 'Authenticate Player Callback',
    type: 'text',
    required: false
  },
  [INTEGRATION_SETTINGS_FIELDS.EVENTS_WEBHOOK_URL]: {
    name: INTEGRATION_SETTINGS_FIELDS.EVENTS_WEBHOOK_URL,
    label: 'Events v1 Webhook',
    type: 'text',
    required: false
  },
  [INTEGRATION_SETTINGS_FIELDS.EVENTS_V2_WEBHOOK_URL]: {
    name: INTEGRATION_SETTINGS_FIELDS.EVENTS_V2_WEBHOOK_URL,
    label: 'Events v2 Webhook',
    type: 'text',
    required: false
  },
  [INTEGRATION_SETTINGS_FIELDS.API_URL_PRIMARY_KEY]: {
    name: INTEGRATION_SETTINGS_FIELDS.API_URL_PRIMARY_KEY,
    label: 'Main Key',
    type: 'password',
    required: false,
    enableCopy: true
  },
  [INTEGRATION_SETTINGS_FIELDS.CHECKOUT_PUBLIC_KEY]: {
    name: INTEGRATION_SETTINGS_FIELDS.CHECKOUT_PUBLIC_KEY,
    label: 'Checkout Public Key',
    type: 'text',
    required: false,
    enableCopy: true,
    disabled: true
  },
  [INTEGRATION_SETTINGS_FIELDS.PUBLISHER_TOKEN]: {
    name: INTEGRATION_SETTINGS_FIELDS.PUBLISHER_TOKEN,
    label: 'Publisher Token',
    type: 'password',
    required: false,
    enableCopy: true,
    disabled: true
  },
  [INTEGRATION_SETTINGS_FIELDS.DEEPLINK_ON]: {
    name: INTEGRATION_SETTINGS_FIELDS.DEEPLINK_ON,
    label: 'Enable JWT Secret',
    type: 'boolean'
  },
  [INTEGRATION_SETTINGS_FIELDS.DEEPLINK_JWT_SECRET]: {
    name: INTEGRATION_SETTINGS_FIELDS.DEEPLINK_JWT_SECRET,
    label: 'Deeplink JWT Secret',
    type: 'password',
    required: false,
    enableCopy: true,
    disabled: true
  },
  [INTEGRATION_SETTINGS_FIELDS.APPSFLYER_INTEGRATION_ON]: {
    name: INTEGRATION_SETTINGS_FIELDS.APPSFLYER_INTEGRATION_ON,
    label: 'Enable AppsFlyer Integration',
    type: 'boolean'
  },
  [INTEGRATION_SETTINGS_FIELDS.APPSFLYER_S2S_TOKEN]: {
    name: INTEGRATION_SETTINGS_FIELDS.APPSFLYER_S2S_TOKEN,
    label: 'AppsFlyer S2S token',
    type: 'text',
    required: false,
    placeholder: 'S2S token'
  },
  [INTEGRATION_SETTINGS_FIELDS.APPSFLYER_APPLICATION_ID]: {
    name: INTEGRATION_SETTINGS_FIELDS.APPSFLYER_APPLICATION_ID,
    label: 'Application ID',
    type: 'text',
    required: false,
    placeholder: 'Application ID'
  }
};

// Predefined field groups
export const fieldGroups = {
  basic: [
    INTEGRATION_SETTINGS_FIELDS.ORDERS_REPORTING_API_URL,
    INTEGRATION_SETTINGS_FIELDS.AWARDING_V2_API_URL,
    INTEGRATION_SETTINGS_FIELDS.AWARDING_V2_RATIO,
    INTEGRATION_SETTINGS_FIELDS.PLAYER_INFO_SYNC_URL,
    INTEGRATION_SETTINGS_FIELDS.PLAYERS_AUTH_WEBHOOK,
    INTEGRATION_SETTINGS_FIELDS.EVENTS_WEBHOOK_URL,
    INTEGRATION_SETTINGS_FIELDS.EVENTS_V2_WEBHOOK_URL,
    INTEGRATION_SETTINGS_FIELDS.API_URL_PRIMARY_KEY,
    INTEGRATION_SETTINGS_FIELDS.CHECKOUT_PUBLIC_KEY,
    INTEGRATION_SETTINGS_FIELDS.PUBLISHER_TOKEN
  ],
  deeplink: [
    INTEGRATION_SETTINGS_FIELDS.DEEPLINK_ON,
    INTEGRATION_SETTINGS_FIELDS.DEEPLINK_JWT_SECRET
  ],
  appsFlyer: [
    INTEGRATION_SETTINGS_FIELDS.APPSFLYER_INTEGRATION_ON,
    INTEGRATION_SETTINGS_FIELDS.APPSFLYER_S2S_TOKEN,
    INTEGRATION_SETTINGS_FIELDS.APPSFLYER_APPLICATION_ID
  ]
};

export const getFieldsByNames = (fieldNames: string[]): IntegrationField[] => {
  return fieldNames
    .map((name) => integrationFields[name])
    .filter((field) => field !== undefined);
};

export const createWebhooksSchema = (fieldNames: string[]) => yup.object().shape({
  ordersReportingApiUrl: yup
    .string()
    .test(
      'at-least-one-required',
      'At least one Grant Award Callback URL must be provided',
      function (value: any) {
        const { awardingV2ApiUrl } = this.parent;
        const hasOrdersReporting = value && value.trim() !== '';
        const hasAwardingV2 = awardingV2ApiUrl && awardingV2ApiUrl.trim() !== '';
        return hasOrdersReporting || hasAwardingV2;
      }
    )
    .test(
      'Must be a valid URL, starting with https://, and not an IP address',
      'Must be a valid URL, starting with https://, and not an IP address',
      (value: any) => !value || IS_NO_IP_ONLY_HTTPS(value as string)
    ),
  awardingV2ApiUrl: yup
    .string()
    .test(
      'at-least-one-required',
      'At least one Grant Award Callback URL must be provided',
      function (value: any) {
        const { ordersReportingApiUrl } = this.parent;
        const hasOrdersReporting = ordersReportingApiUrl && ordersReportingApiUrl.trim() !== '';
        const hasAwardingV2 = value && value.trim() !== '';
        return hasOrdersReporting || hasAwardingV2;
      }
    )
    .test(
      'Must be a valid URL, starting with https://, and not an IP address',
      'Must be a valid URL, starting with https://, and not an IP address',
      (value: any) => !value || IS_NO_IP_ONLY_HTTPS(value as string)
    ),
  awardingV2Ratio: yup.number().min(0).max(100).default(0),
  deeplinkOn: yup.boolean(),
  playerInfoSyncUrl: yup
    .string()
    .test(
      'Must be a valid URL, starting with https://, and not an IP address',
      'Must be a valid URL, starting with https://, and not an IP address',
      (value: any) => IS_NO_IP_ONLY_HTTPS(value as string)
    ),
  eventsWebhookUrl: yup
    .string()
    .test(
      'Must be a valid URL, starting with https://, and not an IP address',
      'Must be a valid URL, starting with https://, and not an IP address',
      (value: any) => IS_NO_IP_ONLY_HTTPS(value as string)
    ),
  eventsV2WebhookUrl: yup
    .string()
    .test(
      'Must be a valid URL, starting with https://, and not an IP address',
      'Must be a valid URL, starting with https://, and not an IP address',
      (value: any) => IS_NO_IP_ONLY_HTTPS(value as string)
    ),
  playersAuthWebhook: yup
    .string()
    .test(
      'Must be a valid URL, starting with https://, and not an IP address',
      'Must be a valid URL, starting with https://, and not an IP address',
      (value: any) => IS_NO_IP_ONLY_HTTPS(value as string)
    ),
  apiUrlPrimaryKey: fieldNames.includes('apiUrlPrimaryKey')
    ? yup.string().required(required('Main Key'))
    : yup.string(),
  appsFlyerIntegrationOn: yup.boolean(),
  appsFlyerS2SToken: yup.string().when('appsFlyerIntegrationOn', {
    is: true,
    then: yup.string().required('AppsFlyer S2S token is required'),
    otherwise: yup.string().notRequired()
  }),
  appsFlyerApplicationID: yup.string().when('appsFlyerIntegrationOn', {
    is: true,
    then: yup.string().required('Application ID is required'),
    otherwise: yup.string().notRequired()
  })
});

export const getInitialValues = (
  fieldNames: string[],
  data: Record<string, any>,
  isAwardingV2Enabled: boolean
): IntegrationFormValues => {
  const values: IntegrationFormValues = {};
  const fields = getFieldsByNames(fieldNames);

  const isAppsFlyerField = (
    name: string
  ): name is (typeof fieldGroups.appsFlyer)[number] =>
    fieldGroups.appsFlyer.includes(name as any);

  fields.forEach((field) => {
    const source = isAppsFlyerField(field.name)
      ? data?.appsFlyerIntegration
      : data;

    values[field.name] =
      field.type === 'boolean'
        ? !!source?.[field.name]
        : source?.[field.name] || '';
  });

  if (isAwardingV2Enabled) {
    values[INTEGRATION_SETTINGS_FIELDS.AWARDING_V2_RATIO] = data?.awardingV2Ratio || 0;
    values[INTEGRATION_SETTINGS_FIELDS.ORDERS_REPORTING_API_URL] = data?.ordersReportingApiUrl || '';
    values[INTEGRATION_SETTINGS_FIELDS.AWARDING_V2_API_URL] = data?.awardingV2ApiUrl || '';
  }

  return values;
};
